<template>
  <div class="company-profile">
    <div class="cp-header">
      <div>
        <h5 class="title cp-title">Company Profile</h5>
        <p class="cp-subtitle">Used on employee payslips and the Policies page.</p>
      </div>
    </div>

    <div class="cp-body">
      <!-- Logo -->
      <div class="cp-logo">
        <span class="cp-label">Company Logo</span>
        <div
          class="cp-logo-box"
          :class="{ 'has-logo': logoPreview, disabled: !isAdmin }"
          @click="openFilePicker"
        >
          <img v-if="logoPreview" :src="logoPreview" alt="Company logo" />
          <div v-else class="cp-logo-empty">
            <i class="fa fa-cloud-upload-alt"></i>
            <span>Upload logo</span>
            <small>PNG, JPG, SVG or WEBP, up to 1 MB</small>
          </div>
        </div>
        <input ref="logoInput" type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" hidden
          @change="onLogoSelected" />
        <div v-if="isAdmin && logoPreview" class="cp-logo-actions">
          <button type="button" class="cp-link" @click="openFilePicker">Change</button>
          <button type="button" class="cp-link cp-link--danger" @click="removeLogo">Remove</button>
        </div>
      </div>

      <!-- Fields -->
      <div class="cp-fields">
        <div class="cp-field">
          <label class="cp-label" for="companyEmail">Email</label>
          <div class="cp-input-wrap">
            <i class="fa fa-envelope"></i>
            <input id="companyEmail" type="email" class="cp-input" v-model.trim="form.companyEmail"
              :disabled="!isAdmin" placeholder="info@company.com" />
          </div>
        </div>
        <div class="cp-field">
          <label class="cp-label" for="companyPhone">Phone</label>
          <div class="cp-input-wrap">
            <i class="fa fa-phone"></i>
            <input id="companyPhone" type="text" class="cp-input" v-model.trim="form.companyPhone"
              :disabled="!isAdmin" placeholder="(021) 1234567" />
          </div>
        </div>
        <div class="cp-field cp-field--full">
          <label class="cp-label" for="companyAddress">Address</label>
          <div class="cp-input-wrap">
            <i class="fa fa-map-marker-alt"></i>
            <input id="companyAddress" type="text" class="cp-input" v-model.trim="form.companyAddress"
              :disabled="!isAdmin" placeholder="Office address" />
          </div>
        </div>
        <div class="cp-field cp-field--full">
          <label class="cp-label" for="policyUrl">Company Policy URL</label>
          <div class="cp-input-wrap">
            <i class="fa fa-file-alt"></i>
            <input id="policyUrl" type="url" class="cp-input" v-model.trim="form.policyUrl"
              :disabled="!isAdmin" placeholder="https://docs.google.com/document/d/..." />
            <a v-if="isValidUrl(form.policyUrl)" :href="form.policyUrl" target="_blank" rel="noopener"
              class="cp-open" title="Open">
              <i class="fa fa-external-link-alt"></i>
            </a>
          </div>
          <small class="cp-help">Shown on the Policies page. Google Docs links are embedded automatically.</small>
        </div>
      </div>
    </div>

    <div v-if="isAdmin" class="cp-footer">
      <button type="button" class="cp-btn cp-btn--ghost" :disabled="!isDirty || saving" @click="reset">
        Discard
      </button>
      <button type="button" class="cp-btn cp-btn--primary" :disabled="!isDirty || saving" @click="save">
        {{ saving ? "Saving..." : "Save Changes" }}
      </button>
    </div>
  </div>
</template>

<script>
import firebase from "../../../assets/config/firebase";
import { uploadToCloudinary } from "../../../utils/cloudinary";
import Swal from "sweetalert";
import { DEFAULT_POLICY_URL } from "src/assets/config/constants.js";
import { createActivityPayload, formatValue, getUpdatedFields, logAdminActivity } from "../../../utils/adminLogs";

const FIELDS = ["companyAddress", "companyPhone", "companyEmail", "companyLogoUrl", "policyUrl"];
const MAX_LOGO_BYTES = 1024 * 1024;

export default {
  name: "CompanyDetails",
  data() {
    return {
      form: this.loadForm(),
      pendingLogo: "", // data URL preview of a newly picked logo
      pendingLogoFile: null, // uploaded on save
      saving: false,
    };
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user || {};
    },
    isAdmin() {
      return this.currentUser.role === "admin";
    },
    logoPreview() {
      return this.pendingLogo || this.form.companyLogoUrl;
    },
    isDirty() {
      const saved = this.loadForm();
      return !!this.pendingLogo || FIELDS.some((key) => saved[key] !== this.form[key]);
    },
  },
  methods: {
    loadForm() {
      const constants = this.$store.state.auth.constants || {};
      const form = {};
      FIELDS.forEach((key) => {
        form[key] = constants[key] || "";
      });
      form.policyUrl = form.policyUrl || DEFAULT_POLICY_URL;
      return form;
    },
    isValidUrl(url) {
      return /^https?:\/\/\S+$/i.test(url || "");
    },
    openFilePicker() {
      if (this.isAdmin) this.$refs.logoInput.click();
    },
    onLogoSelected(event) {
      const file = event.target.files && event.target.files[0];
      event.target.value = "";
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        Swal("Invalid file", "Please choose an image file.", "error");
        return;
      }
      if (file.size > MAX_LOGO_BYTES) {
        Swal("File too large", "Logo must be 1 MB or smaller.", "error");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        this.pendingLogo = reader.result;
        this.pendingLogoFile = file;
      };
      reader.readAsDataURL(file);
    },
    removeLogo() {
      this.pendingLogo = "";
      this.pendingLogoFile = null;
      this.form.companyLogoUrl = "";
    },
    reset() {
      this.form = this.loadForm();
      this.pendingLogo = "";
      this.pendingLogoFile = null;
    },
    async save() {
      const { companyEmail, policyUrl } = this.form;
      if (companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(companyEmail)) {
        Swal("Invalid email", "Please enter a valid company email.", "error");
        return;
      }
      if (policyUrl && !this.isValidUrl(policyUrl)) {
        Swal("Invalid URL", "Company Policy URL must start with http:// or https://", "error");
        return;
      }

      const constants = this.$store.state.auth.constants || {};
      const oldValues = {};
      FIELDS.forEach((key) => {
        oldValues[key] = constants[key] || "";
      });

      this.saving = true;
      this.$store.commit("loadingStarts");
      try {
        const updated = { ...this.form };
        if (this.pendingLogoFile) {
          updated.companyLogoUrl = await uploadToCloudinary(this.pendingLogoFile, "company_logo", "profile_images");
        }
        await firebase.updateDatabase("constants", updated);
        this.$store.commit("setConstants", { ...constants, ...updated });
        this.form = { ...updated };
        this.pendingLogo = "";
        this.pendingLogoFile = null;
        Swal("Success!", "Company profile saved!", "success");

        const changes = Object.entries(getUpdatedFields(oldValues, updated))
          .map(([key, value]) => `${key}: ${formatValue(value.old)} → ${formatValue(value.new)}`)
          .join("\n");
        await logAdminActivity(
          createActivityPayload({
            user: this.currentUser,
            title: `${this.currentUser.username} updated company profile`,
            description: `${this.currentUser.email} updated company profile.`,
            changes,
            type: "settings",
          })
        );
      } catch (error) {
        console.error("Failed to save company profile:", error);
        Swal("Error!", "Failed to save company profile.", "error");
      } finally {
        this.saving = false;
        this.$store.commit("loadingEnds");
      }
    },
  },
};
</script>

<style scoped>
.company-profile {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 20px 24px;
  background: #fff;
}

.cp-title {
  margin: 0;
}

.cp-subtitle {
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 13px;
}

.cp-body {
  display: flex;
  gap: 28px;
  margin-top: 20px;
}

.cp-label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 6px;
}

/* Logo */
.cp-logo {
  flex: 0 0 200px;
}

.cp-logo-box {
  width: 200px;
  height: 140px;
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #f9fafb;
  transition: border-color 0.2s, background 0.2s;
  overflow: hidden;
}

.cp-logo-box:hover:not(.disabled) {
  border-color: #435851;
  background: #f3f6f5;
}

.cp-logo-box.has-logo {
  border-style: solid;
  background: #fff;
}

.cp-logo-box.disabled {
  cursor: default;
}

.cp-logo-box img {
  max-width: 85%;
  max-height: 85%;
  object-fit: contain;
}

.cp-logo-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: #6b7280;
  font-size: 13px;
  text-align: center;
  padding: 0 10px;
}

.cp-logo-empty i {
  font-size: 26px;
  color: #435851;
}

.cp-logo-empty small {
  font-size: 11px;
  color: #9ca3af;
}

.cp-logo-actions {
  display: flex;
  gap: 14px;
  margin-top: 8px;
}

.cp-link {
  background: none;
  border: none;
  padding: 0;
  color: #435851;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.cp-link--danger {
  color: #dc2626;
}

/* Fields */
.cp-fields {
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
  align-content: start;
}

.cp-field--full {
  grid-column: 1 / -1;
}

.cp-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
}

.cp-input-wrap > i {
  position: absolute;
  left: 12px;
  color: #9ca3af;
  font-size: 14px;
  pointer-events: none;
}

.cp-input {
  width: 100%;
  height: 40px;
  padding: 0 38px 0 36px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: #fff;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.cp-input:focus {
  outline: none;
  border-color: #435851;
  box-shadow: 0 0 0 3px rgba(67, 88, 81, 0.15);
}

.cp-input:disabled {
  background: #f3f4f6;
  color: #6b7280;
}

.cp-open {
  position: absolute;
  right: 12px;
  color: #435851;
}

.cp-help {
  display: block;
  margin-top: 5px;
  color: #9ca3af;
  font-size: 12px;
}

/* Footer */
.cp-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid #f1f1f1;
}

.cp-btn {
  min-width: 110px;
  height: 38px;
  padding: 0 18px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.2s;
}

.cp-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.cp-btn--primary {
  background: #435851;
  color: #fff;
}

.cp-btn--ghost {
  background: #fff;
  color: #374151;
  border-color: #d1d5db;
}

@media (max-width: 768px) {
  .cp-body {
    flex-direction: column;
  }

  .cp-logo {
    flex-basis: auto;
  }

  .cp-fields {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/CompanyDetails.vue